<template>
  <div class="ncrm-activity-card" :class="{ 'ncrm-activity-card--closed': isClosed }">
    <span class="ncrm-activity-card__node" :style="{ backgroundColor: accentColor }" aria-hidden="true">
      <v-icon size="13" color="white">{{ iconName }}</v-icon>
    </span>

    <div
      class="ncrm-activity-card__body"
      :class="{ 'ncrm-activity-card__body--accent': showAccent }"
      :style="showAccent ? { borderLeftColor: accentColor } : null"
    >
      <div class="ncrm-activity-card__head">
        <span
          class="ncrm-activity-card__title"
          :class="{ 'ncrm-activity-card__title--closed': isClosed }"
        >
          {{ title }}
        </span>
        <span v-if="content" class="ncrm-activity-card__content">— {{ content }}</span>
        <span class="ncrm-activity-card__date">{{ dateText }}</span>

        <v-btn
          v-if="recordingAvailable"
          icon="mdi-download"
          size="x-small"
          variant="text"
          color="primary"
          class="ncrm-activity-card__recording-btn"
          :loading="recordingLoading"
          :disabled="recordingLoading"
          :title="$t('ncrm.integrations.pbx.actions.downloadRecording')"
          :aria-label="$t('ncrm.integrations.pbx.actions.downloadRecording')"
          @click.stop="$emit('download-recording')"
        />

        <v-menu v-if="allowCreate && canManage" location="bottom end">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-dots-vertical"
              size="x-small"
              variant="text"
              class="ncrm-activity-card__menu-btn"
              :disabled="busy || actionLoading"
              :aria-label="$t('ncrm.activity.actions.more')"
            />
          </template>
          <v-list density="compact" class="py-1">
            <v-list-item @click="$emit('edit')">
              <template #prepend>
                <v-icon color="primary">mdi-pencil</v-icon>
              </template>
              <v-list-item-title>{{ $t("ncrm.activity.actions.edit") }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="canClose" :disabled="actionLoading" @click="$emit('close')">
              <template #prepend>
                <v-icon color="success">mdi-check</v-icon>
              </template>
              <v-list-item-title>{{ $t("ncrm.activity.actions.close") }}</v-list-item-title>
            </v-list-item>
            <v-list-item :disabled="busy" @click="$emit('delete')">
              <template #prepend>
                <v-icon color="error">mdi-delete</v-icon>
              </template>
              <v-list-item-title class="text-error">{{ $t("ncrm.activity.actions.delete") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <div v-if="showMetaRow" class="ncrm-activity-card__meta">
        <div class="ncrm-activity-card__meta-left">
          <v-chip v-if="statusLabel" size="x-small" :color="statusColor">
            {{ statusLabel }}
          </v-chip>
          <v-chip v-if="priorityLabel" size="x-small" :color="priorityColor">
            {{ priorityLabel }}
          </v-chip>
          <v-chip
            v-if="originContext"
            size="x-small"
            :variant="originClickable ? 'tonal' : 'outlined'"
            prepend-icon="mdi-source-branch"
            class="ncrm-activity-card__origin"
            :class="{ 'ncrm-activity-card__origin--clickable': originClickable }"
            @click="originClickable && $emit('open-origin')"
          >
            {{ originContext }}
          </v-chip>
        </div>

        <div v-if="allowCreate && canClose" class="ncrm-activity-card__meta-right">
          <v-btn
            v-if="pending"
            size="small"
            variant="tonal"
            color="warning"
            class="ncrm-activity-card__undo-btn"
            :aria-label="$t('ncrm.activity.actions.undo')"
            @click.stop="$emit('undo')"
          >
            <v-icon start size="15">mdi-undo-variant</v-icon>
            {{ $t("ncrm.activity.actions.undo") }}
            <span class="ncrm-activity-card__undo-count">{{ pending.remaining }}</span>
          </v-btn>
          <v-btn
            v-else
            size="small"
            variant="tonal"
            color="success"
            class="ncrm-activity-card__done-btn"
            :loading="actionLoading"
            :disabled="busy || actionLoading"
            :aria-label="$t('ncrm.activity.actions.markDone')"
            @click.stop="$emit('close')"
          >
            <v-icon start size="15">mdi-check</v-icon>
            {{ $t("ncrm.activity.actions.markDone") }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const THEME_ACCENT_KEYS = new Set(["primary", "secondary", "tertiary", "success", "warning", "error", "info"]);

export default {
  name: "NcrmActivityCard",
  props: {
    title: { type: String, default: "" },
    content: { type: String, default: "" },
    dateText: { type: String, default: "" },
    iconName: { type: String, default: "mdi-note-text-outline" },
    statusLabel: { type: String, default: "" },
    statusColor: { type: String, default: "primary" },
    priorityLabel: { type: String, default: "" },
    priorityColor: { type: String, default: "primary" },
    originContext: { type: String, default: "" },
    originClickable: { type: Boolean, default: false },
    accentColorKey: { type: String, default: "primary" },
    isNote: { type: Boolean, default: false },
    isClosed: { type: Boolean, default: false },
    canClose: { type: Boolean, default: false },
    canManage: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: false },
    pending: { type: Object, default: null },
    actionLoading: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    recordingAvailable: { type: Boolean, default: false },
    recordingLoading: { type: Boolean, default: false },
  },
  emits: ["close", "undo", "edit", "delete", "open-origin", "download-recording"],
  computed: {
    accentColor() {
      const key = THEME_ACCENT_KEYS.has(this.accentColorKey) ? this.accentColorKey : "primary";
      return `rgb(var(--v-theme-${key}))`;
    },
    showAccent() {
      return !this.isClosed && !this.isNote;
    },
    showMetaRow() {
      return Boolean(
        this.statusLabel
        || this.priorityLabel
        || this.originContext
        || (this.allowCreate && this.canClose)
      );
    },
  },
};
</script>

<style scoped>
.ncrm-activity-card {
  position: relative;
}

.ncrm-activity-card + .ncrm-activity-card {
  margin-top: 12px;
}

.ncrm-activity-card__node {
  position: absolute;
  left: -28px;
  top: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--app-radius-pill);
  border: 3px solid rgb(var(--v-theme-surface));
  box-shadow: 0 0 0 1px var(--ncrm-color-border-default);
  z-index: 1;
}

.ncrm-activity-card__body {
  background: rgb(var(--v-theme-surface));
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  padding: 12px 14px;
  box-shadow: var(--app-shadow-sm);
  transition: box-shadow 150ms ease;
}

.ncrm-activity-card__body:hover {
  box-shadow: var(--app-shadow-md);
}

.ncrm-activity-card__body--accent {
  border-left-width: 3px;
  border-left-style: solid;
}

.ncrm-activity-card--closed .ncrm-activity-card__body {
  opacity: 0.72;
  box-shadow: none;
}

.ncrm-activity-card__head {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.ncrm-activity-card__title {
  flex-shrink: 0;
  max-width: 55%;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.35;
  color: var(--ncrm-color-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ncrm-activity-card__title--closed {
  text-decoration: line-through;
  text-decoration-color: var(--ncrm-color-border-strong);
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.ncrm-activity-card__content {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), 0.55);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ncrm-activity-card--closed .ncrm-activity-card__content {
  color: rgba(var(--v-theme-on-surface), 0.4);
}

.ncrm-activity-card__date {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 11px;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.4);
  white-space: nowrap;
}

.ncrm-activity-card__menu-btn {
  flex-shrink: 0;
  margin: -4px -6px -4px 0;
}

.ncrm-activity-card__recording-btn {
  flex-shrink: 0;
  margin: -4px 0;
}

.ncrm-activity-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}

.ncrm-activity-card__meta-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.ncrm-activity-card__meta-right {
  flex-shrink: 0;
}

.ncrm-activity-card__origin {
  max-width: 100%;
  border-color: var(--ncrm-color-origin-chip-border) !important;
  background-color: var(--ncrm-color-origin-chip-bg) !important;
  color: var(--ncrm-color-origin-chip-text) !important;
}

.ncrm-activity-card__origin--clickable {
  cursor: pointer;
}

.ncrm-activity-card__done-btn,
.ncrm-activity-card__undo-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  border-radius: var(--app-radius-base);
}

.ncrm-activity-card__undo-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding-inline: 4px;
  border-radius: var(--app-radius-pill);
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background-color: rgba(var(--v-theme-on-surface), 0.12);
}
</style>
